import { useCallback, useEffect, useRef, useState } from 'react';
import type { FlowNode, FlowEdge } from '../types';

interface Snapshot {
  nodes: FlowNode[];
  edges: FlowEdge[];
  ts: number;
}

interface Options {
  max?: number;
  debounceMs?: number;
}

/**
 * Snapshot-based undo/redo. Watches `nodes` and `edges`. After a quiet period,
 * pushes a deep-cloned snapshot onto the history stack.
 *
 * Undo pops back; redo replays forward. Returns the current cursor + helpers.
 */
export function useHistory(
  nodes: FlowNode[],
  edges: FlowEdge[],
  apply: (s: Snapshot) => void,
  opts: Options = {}
) {
  const max = opts.max ?? 60;
  const debounceMs = opts.debounceMs ?? 350;

  const stackRef = useRef<Snapshot[]>([]);
  const indexRef = useRef<number>(-1);   // index of "current" snapshot
  const lastPushedRef = useRef<string>('');
  const timerRef = useRef<number | null>(null);
  const restoringRef = useRef<boolean>(false);

  // Force re-render on push/undo/redo for canUndo/canRedo
  const [, bump] = useState(0);
  const rerender = useCallback(() => bump(b => b + 1), []);

  const signature = useCallback(() => {
    return JSON.stringify({ n: nodes.map(n => [n.id, n.position, n.data]), e: edges.map(e => [e.source, e.target, e.sourceHandle, e.label]) });
  }, [nodes, edges]);

  const pushNow = useCallback(() => {
    if (restoringRef.current) return;
    const sig = signature();
    if (sig === lastPushedRef.current) return;
    const snap: Snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
      ts: Date.now(),
    };
    // Drop any "redo" tail
    stackRef.current = stackRef.current.slice(0, indexRef.current + 1);
    stackRef.current.push(snap);
    if (stackRef.current.length > max) {
      stackRef.current = stackRef.current.slice(stackRef.current.length - max);
    }
    indexRef.current = stackRef.current.length - 1;
    lastPushedRef.current = sig;
    rerender();
  }, [nodes, edges, signature, max, rerender]);

  // Debounced auto-snapshot
  useEffect(() => {
    if (restoringRef.current) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      pushNow();
      timerRef.current = null;
    }, debounceMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [nodes, edges, pushNow, debounceMs]);

  // Seed with initial snapshot
  useEffect(() => {
    if (stackRef.current.length === 0 && nodes.length > 0) {
      pushNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const undo = useCallback(() => {
    if (indexRef.current <= 0) return null;
    indexRef.current -= 1;
    const snap = stackRef.current[indexRef.current];
    if (!snap) return null;
    restoringRef.current = true;
    apply({ nodes: JSON.parse(JSON.stringify(snap.nodes)), edges: JSON.parse(JSON.stringify(snap.edges)), ts: snap.ts });
    lastPushedRef.current = JSON.stringify({ n: snap.nodes.map(n => [n.id, n.position, n.data]), e: snap.edges.map(e => [e.source, e.target, e.sourceHandle, e.label]) });
    setTimeout(() => { restoringRef.current = false; }, 50);
    rerender();
    return snap;
  }, [apply, rerender]);

  const redo = useCallback(() => {
    if (indexRef.current >= stackRef.current.length - 1) return null;
    indexRef.current += 1;
    const snap = stackRef.current[indexRef.current];
    if (!snap) return null;
    restoringRef.current = true;
    apply({ nodes: JSON.parse(JSON.stringify(snap.nodes)), edges: JSON.parse(JSON.stringify(snap.edges)), ts: snap.ts });
    lastPushedRef.current = JSON.stringify({ n: snap.nodes.map(n => [n.id, n.position, n.data]), e: snap.edges.map(e => [e.source, e.target, e.sourceHandle, e.label]) });
    setTimeout(() => { restoringRef.current = false; }, 50);
    rerender();
    return snap;
  }, [apply, rerender]);

  const clear = useCallback(() => {
    stackRef.current = [];
    indexRef.current = -1;
    lastPushedRef.current = '';
    rerender();
  }, [rerender]);

  return {
    undo,
    redo,
    canUndo: indexRef.current > 0,
    canRedo: indexRef.current < stackRef.current.length - 1,
    historyLength: stackRef.current.length,
    clear,
  };
}
